import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "./Header";
import PI from "../../assets/PI.png";
import config from "../../config";
import VertNav from "./VertNav";
import { FiMapPin, FiUpload, FiSave, FiLink } from "react-icons/fi";

function PersonalInformation() {
    const [P, setPI] = useState({});
    const [on, setOn] = useState("");
    const [e, setE] = useState("");
    const [gn, setGn] = useState("");
    const [pn, setPn] = useState("");
    const [p, setP] = useState("");
    const [address, setAddress] = useState("");
    const [location, setLocation] = useState({ lat: null, lng: null });
    const [accuracy, setAccuracy] = useState(null);
    const [isLocating, setIsLocating] = useState(false);
    const [reviewLink, setReviewLink] = useState("");

    const no = atob(localStorage.getItem("number"));
    const bid = localStorage.getItem("branch_id");

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(
                    `${config.apiUrl}/api/swalook/get_current_user/?id=${no}&branch_name=${bid}`,
                    {
                        headers: {
                            Authorization: `Token ${token}`,
                        },
                    }
                );

                if (response.data.status && response.data.current_user_data) {
                    const userData = response.data.current_user_data;
                    setPI(userData);
                    console.log("User Data:", userData);

                    // Store important data in localStorage
                    localStorage.setItem(
                        "profile_pic",
                        userData.profile_pic || ""
                    );
                    localStorage.setItem("mobile_no", userData.mobile_no || "");
                    localStorage.setItem("review_link", userData.review || "");

                    // Set location if available
                    if (userData.latitude && userData.longitude) {
                        setLocation({
                            lat: parseFloat(userData.latitude),
                            lng: parseFloat(userData.longitude),
                        });
                    }

                    // Set address if available (you might need to add address field to your API)
                    if (userData.address) {
                        setAddress(userData.address);
                    }

                    // Set review link
                    if (userData.review) {
                        setReviewLink(userData.review);
                    }

                    // Set logo preview if profile pic exists
                    if (userData.profile_pic) {
                        setLogoPreview(
                            `${config.apiUrl}${userData.profile_pic}`
                        );
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [no, bid]);

    useEffect(() => {
        if (P) {
            setOn(P.owner_name || "");
            setE(P.email || "");
            setGn(P.gst_number || "");
            setPn(P.pan_number || "");
            setP(P.pincode || "");
            setReviewLink(P.review || "");

            // Set address if available in the API response
            if (P.address) {
                setAddress(P.address);
            }

            // Set location if available
            if (P.latitude && P.longitude) {
                setLocation({
                    lat: parseFloat(P.latitude),
                    lng: parseFloat(P.longitude),
                });
            }
        }
    }, [P]);

    const handleUpdate = async () => {
        try {
            const formData = new FormData();
            formData.append("owner_name", on);
            formData.append("email", e);
            formData.append("gst_number", gn);
            formData.append("pan_number", pn);
            formData.append("pincode", p);
            formData.append("address", address);
            formData.append("latitude", location.lat);
            formData.append("longitude", location.lng);
            formData.append("review", reviewLink);

            if (logoFile) {
                formData.append("profile_pic", logoFile);
            }

            console.log("Updating profile...", {
                owner_name: on,
                email: e,
                gst_number: gn,
                pan_number: pn,
                pincode: p,
                address: address,
                location: location,
                review: reviewLink,
            });

            await axios.put(
                `${config.apiUrl}/api/swalook/edit/profile/?id=${no}`,
                formData,
                {
                    headers: {
                        Authorization: `Token ${localStorage.getItem("token")}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            // Save review link to localStorage
            localStorage.setItem("review_link", reviewLink);

            alert("Profile updated successfully");
        } catch (err) {
            console.error("Update error:", err.response?.data || err);
            alert("Failed to update profile");
        }
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            setIsLocating(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                    setAccuracy(position.coords.accuracy);
                    setIsLocating(false);
                    fetchAddress(
                        position.coords.latitude,
                        position.coords.longitude
                    );
                },
                (error) => {
                    console.error("Error getting location:", error);
                    setIsLocating(false);
                    alert(
                        "Could not get your location. Please ensure location services are enabled."
                    );
                },
                {
                    enableHighAccuracy: true,
                    timeout: 100000,
                    maximumAge: 0,
                }
            );
        } else {
            alert("Geolocation is not supported by this browser.");
        }
    };

    const fetchAddress = async (lat, lng) => {
        try {
            const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
            );
            const { display_name } = response.data;
            setAddress(display_name);
        } catch (error) {
            console.error("Error fetching address:", error);
        }
    };

    return (
        <>
            <Header />
            <VertNav />
            <div className="bg-white min-h-screen p-4">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row items-start justify-center gap-8">
                        {/* Left side - Image */}
                        <div className="w-full md:w-1/2 lg:w-1/3 flex justify-center">
                            <img
                                src={PI}
                                alt="Salon"
                                className="max-w-screen md:max-w-screen w-full rounded-lg"
                            />
                        </div>

                        {/* Right side - Form */}
                        <div className="w-full md:w-1/2 lg:w-2/3 bg-white p-6">
                            <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">
                                Personal Details
                            </h2>
                            <hr className="mb-6" />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Salon Name */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="saloonName"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Salon Name
                                    </label>
                                    <input
                                        type="text"
                                        id="saloonName"
                                        value={P.salon_name || ""}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Owner Name */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="ownerName"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Owner Name
                                    </label>
                                    <input
                                        type="text"
                                        id="ownerName"
                                        value={on}
                                        onChange={(e) => setOn(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Phone Number */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="phoneNumber"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        id="phoneNumber"
                                        value={P.mobile_no || ""}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Email */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="emailId"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="emailId"
                                        value={e}
                                        onChange={(e) => setE(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* GST Number */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="gstNumber"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        GST Number
                                    </label>
                                    <input
                                        type="text"
                                        id="gstNumber"
                                        value={gn}
                                        onChange={(e) => setGn(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* PAN Number */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="panNumber"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        PAN Number
                                    </label>
                                    <input
                                        type="text"
                                        id="panNumber"
                                        value={pn}
                                        onChange={(e) => setPn(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Pincode */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="pincode"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Pincode
                                    </label>
                                    <input
                                        type="number"
                                        id="pincode"
                                        value={p}
                                        onChange={(e) => setP(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Vendor ID */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="vendorId"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Vendor ID
                                    </label>
                                    <input
                                        type="text"
                                        id="vendorId"
                                        value={P.vendor_id || ""}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Account Created Date */}
                                <div className="mb-4">
                                    <label
                                        htmlFor="createdDate"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Account Created Date
                                    </label>
                                    <input
                                        type="text"
                                        id="createdDate"
                                        value={P.account_created_date || ""}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Google Review Link */}
                                <div className="mb-4 col-span-1 md:col-span-2">
                                    <label
                                        htmlFor="reviewLink"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Links shared over Whatsapp{" "}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <FiLink className="text-gray-500" />
                                        <input
                                            type="url"
                                            id="reviewLink"
                                            value={reviewLink}
                                            onChange={(e) =>
                                                setReviewLink(e.target.value)
                                            }
                                            placeholder="https://g.page/r/your-business/review"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Add your Google My Business review link
                                        for customers to leave reviews
                                    </p>
                                </div>

                                {/* Salon Logo */}
                                <div className="mb-4 col-span-1 md:col-span-2">
                                    <label
                                        htmlFor="logoUpload"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Salon Logo
                                    </label>
                                    <div className="flex items-center gap-4">
                                        <label className="flex flex-col items-center px-4 py-2 bg-white text-blue-500 rounded-lg border border-blue-500 cursor-pointer hover:bg-blue-50">
                                            <FiUpload className="w-5 h-5" />
                                            <span className="mt-1 text-sm">
                                                Choose File
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                id="logoUpload"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file =
                                                        e.target.files[0];
                                                    if (file) {
                                                        setLogoFile(file);
                                                        setLogoPreview(
                                                            URL.createObjectURL(
                                                                file
                                                            )
                                                        );
                                                    }
                                                }}
                                            />
                                        </label>
                                        {logoPreview && (
                                            <img
                                                src={logoPreview}
                                                alt="Logo Preview"
                                                className="w-24 h-24 object-cover rounded-md border"
                                            />
                                        )}
                                    </div>
                                    {P.profile_pic && !logoPreview && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            Current logo: {P.profile_pic}
                                        </p>
                                    )}
                                </div>

                                {/* Location */}
                                <div className="mb-4 col-span-1 md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Salon Location
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={address}
                                            onChange={(e) =>
                                                setAddress(e.target.value)
                                            }
                                            placeholder="Salon address"
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <button
                                            type="button"
                                            onClick={getCurrentLocation}
                                            disabled={isLocating}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300"
                                        >
                                            <FiMapPin />
                                            {isLocating
                                                ? "Locating..."
                                                : "Get Location"}
                                        </button>
                                    </div>
                                    {location.lat && location.lng && (
                                        <div className="mt-2 text-xs text-gray-500">
                                            Coordinates:{" "}
                                            {location.lat.toFixed(6)},{" "}
                                            {location.lng.toFixed(6)}
                                            {accuracy && (
                                                <span className="ml-2">
                                                    (Accuracy:{" "}
                                                    {accuracy.toFixed(1)}{" "}
                                                    meters)
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {P.latitude &&
                                        P.longitude &&
                                        !location.lat && (
                                            <div className="mt-2 text-xs text-gray-500">
                                                Current Coordinates:{" "}
                                                {P.latitude}, {P.longitude}
                                            </div>
                                        )}
                                </div>
                            </div>

                            {/* Update Button */}
                            <div className="mt-6 flex justify-center">
                                <button
                                    onClick={handleUpdate}
                                    className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                                >
                                    <FiSave />
                                    Update Profile
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default PersonalInformation;
